/**
 * DraftNumberInput.tsx
 *
 * A number field for the quote editor that keeps what is being typed as text
 * and hands the parent a number. The payment product lines use it too.
 *
 * Binding a controlled input straight to `Number(v) || 0` rewrote the text on
 * every keystroke, so "12." became "12" before the cents could be typed, and a
 * quantity bound to `Number(v) || 1` snapped back to 1 the moment it was
 * cleared, turning a typed "3" into "13".
 *
 * Every keystroke is passed up straight away, so the totals keep following the
 * typing. An empty or half-typed entry ("", "-", ".") stays on screen while
 * `emptyValue` is passed up: Manual Entry's form submits on Enter with the
 * focus still in the field, and must not save the number that was cleared.
 */
import { useState, type InputHTMLAttributes } from 'react';

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode'> {
  value: number;
  onCommit: (value: number) => void;
  /** Whole numbers only, for a quantity. Money takes a decimal point. */
  integer?: boolean;
  /** What an empty field stands for once it is left. */
  emptyValue?: number;
  /** False for a figure that cannot be below zero: the minus sign is refused. */
  allowNegative?: boolean;
  /** Digits allowed after the point, 2 for money; one more is refused. Unlimited when unset. */
  maxDecimals?: number;
  /** Shows an empty field (so the placeholder) instead of "0" while the value is 0. */
  blankZero?: boolean;
}

const DECIMAL = /^-?\d*\.?\d*$/;
const UNSIGNED_DECIMAL = /^\d*\.?\d*$/;
const WHOLE = /^\d*$/;

function parse(text: string): number | null {
  if (!/\d/.test(text)) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

export function DraftNumberInput({
  value, onCommit, integer = false, emptyValue = 0, allowNegative = true,
  maxDecimals, blankZero = false, onBlur, ...rest
}: Props) {
  // null while the field is not being edited, so an outside change shows.
  const [draft, setDraft] = useState<string | null>(null);
  const pattern = integer ? WHOLE : allowNegative ? DECIMAL : UNSIGNED_DECIMAL;

  return (
    <input
      {...rest}
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      value={draft ?? (blankZero && value === 0 ? '' : String(value))}
      onChange={(e) => {
        const text = e.target.value;
        if (!pattern.test(text)) return;
        if (maxDecimals !== undefined && (text.split('.')[1]?.length ?? 0) > maxDecimals) return;

        setDraft(text);
        const n = parse(text) ?? emptyValue;
        if (n !== value) onCommit(n);
      }}
      onBlur={(e) => {
        if (draft !== null) {
          const n = parse(draft) ?? emptyValue;
          if (n !== value) onCommit(n);
          setDraft(null);
        }
        onBlur?.(e);
      }}
    />
  );
}
